import { EyeOff, MessageSquare, Pin, PinOff } from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { Label } from '@/components/ui/field';
import { useAddNote, useSetNotePinned } from '@/hooks/clients';
import { formatInstant } from '@/lib/dates';
import { ApiError } from '@/lib/api';
import { useToast } from '@/lib/toast-context';
import { cn } from '@/lib/utils';
import {
  CLIENT_NOTE_TYPE_LABELS,
  CLIENT_NOTE_TYPE_VALUES,
  type ClientDetail,
  type ClientNoteTypeValue,
} from '@shared/schemas/client';

/**
 * The Notes tab.
 *
 * **Append-only, and migration 030 did not change that.** There is still no edit
 * and no delete: a note records what somebody thought at the time, and the value
 * of it is that it has not been rewritten since. "I told you about the dog" is
 * settled by a note with a date and a name on it.
 *
 * What 030 added is three things that describe a note without touching what it
 * says — a type, a pin and an admin-only flag. Pinning is the one that can
 * change afterwards, and it moves the note rather than editing it. The
 * prototype also offers delete; that is the one thing on its screen we
 * deliberately did not take.
 */
export function ClientNotesTab({ detail }: { detail: ClientDetail }) {
  const toast = useToast();
  const addNote = useAddNote(detail.id);
  const setPinned = useSetNotePinned(detail.id);

  const [body, setBody] = useState('');
  const [noteType, setNoteType] = useState<ClientNoteTypeValue>('general');
  const [isPrivate, setIsPrivate] = useState(false);

  function submit(event: FormEvent) {
    event.preventDefault();
    if (body.trim().length === 0) return;

    addNote.mutate(
      { body, noteType, isPrivate },
      {
        onSuccess: () => {
          toast.success('Note added.');
          setBody('');
          // The type and the flag reset with it. A private complaint followed by
          // an ordinary note should not inherit either by accident.
          setNoteType('general');
          setIsPrivate(false);
        },
        onError: (cause) => {
          toast.error(cause instanceof ApiError ? cause.message : 'That note could not be added.');
        },
      },
    );
  }

  function togglePin(noteId: string, next: boolean) {
    setPinned.mutate(
      { noteId, isPinned: next },
      {
        onSuccess: () => {
          toast.success(next ? 'Pinned to the top.' : 'Unpinned.');
        },
        onError: (cause) => {
          toast.error(cause instanceof ApiError ? cause.message : 'That could not be changed.');
        },
      },
    );
  }

  return (
    <div className="space-y-4">
      <form onSubmit={submit} className="rounded-lg border border-ink-300 bg-white p-4 shadow-card">
        <Label htmlFor="client-note">Add a note</Label>
        <textarea
          id="client-note"
          name="body"
          rows={3}
          value={body}
          onChange={(event) => {
            setBody(event.target.value);
          }}
          className="mt-1.5 w-full rounded-sm border border-ink-300 bg-white px-3 py-2 text-body"
          placeholder="Prefers the back door. Gate code changed in July."
        />

        {/*
          **Defaulted to General rather than required.** A note nobody could be
          bothered to classify is still worth having, and a required dropdown
          would get every note filed as whatever sits at the top of the list.
        */}
        <fieldset className="mt-3">
          <legend className="text-label text-ink-900">What is it about?</legend>
          <div className="mt-2 flex flex-wrap gap-2">
            {CLIENT_NOTE_TYPE_VALUES.map((value) => {
              const selected = noteType === value;
              return (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => {
                    setNoteType(value);
                  }}
                  className={cn(
                    'inline-flex min-h-touch items-center rounded-full px-3.5 text-label',
                    selected
                      ? 'bg-honey-400 text-ink-900'
                      : 'border border-ink-300 bg-white text-ink-700 hover:bg-honey-50',
                  )}
                >
                  {/* The label, never the stored value — `follow_up` must not
                      reach a screen. P5. */}
                  {CLIENT_NOTE_TYPE_LABELS[value]}
                </button>
              );
            })}
          </div>
        </fieldset>

        <label className="mt-3 flex min-h-touch items-center gap-2.5">
          <input
            type="checkbox"
            name="isPrivate"
            checked={isPrivate}
            onChange={(event) => {
              setIsPrivate(event.target.checked);
            }}
            className="size-4 rounded-sm border-ink-300"
          />
          <span className="text-label text-ink-900">Private — the office only</span>
        </label>

        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <p className="text-meta text-ink-500">
            Notes cannot be edited or removed once added — that is what makes them worth having.
          </p>
          <Button type="submit" disabled={addNote.isPending || body.trim().length === 0}>
            {addNote.isPending ? 'Adding…' : 'Add note'}
          </Button>
        </div>
      </form>

      {detail.notes.length === 0 ? (
        <div className="rounded-lg border border-ink-300 bg-white shadow-card">
          <EmptyState
            icon={MessageSquare}
            title="No notes yet."
            hint="Anything the office should remember about this client — where to park, which door, who to ask for."
          />
        </div>
      ) : (
        <ol className="space-y-3">
          {detail.notes.map((note) => (
            <li
              key={note.id}
              className={cn(
                'rounded-lg border bg-white p-4 shadow-card',
                // Pinned notes are already first — the server orders them that
                // way. The border says *why* they are first, so the order does
                // not read as a sorting bug.
                note.isPinned ? 'border-honey-400' : 'border-ink-300',
              )}
            >
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-ink-900/5 px-2 py-0.5 text-meta text-ink-700">
                  {note.noteTypeLabel}
                </span>
                {note.isPinned ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-honey-50 px-2 py-0.5 text-meta text-bark-900">
                    <Pin aria-hidden="true" className="size-3" />
                    Pinned
                  </span>
                ) : null}
                {note.isPrivate ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-ink-900/5 px-2 py-0.5 text-meta text-ink-700">
                    <EyeOff aria-hidden="true" className="size-3" />
                    Private
                  </span>
                ) : null}

                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-auto"
                  disabled={setPinned.isPending}
                  onClick={() => {
                    togglePin(note.id, !note.isPinned);
                  }}
                >
                  {note.isPinned ? (
                    <PinOff aria-hidden="true" className="size-4" />
                  ) : (
                    <Pin aria-hidden="true" className="size-4" />
                  )}
                  {note.isPinned ? 'Unpin' : 'Pin'}
                  <span className="sr-only"> this note</span>
                </Button>
              </div>

              <p className="whitespace-pre-wrap text-body text-ink-900">{note.body}</p>
              <p className="mt-2 text-meta text-ink-500">
                {/* The author's account may since have been deleted; the note
                    survives it, so this reads "Unknown" rather than breaking. */}
                {note.authorName ?? 'Unknown'} · {formatInstant(note.createdAt)}
              </p>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
